import { useState } from "react";
import { adminApi } from "@/lib/api";
import { CalendarRange, DollarSign, Download, Search } from "lucide-react";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function addDaysISO(iso, n) {
  const dt = new Date(iso + "T00:00:00");
  dt.setDate(dt.getDate() + n);
  return dt.toISOString().slice(0, 10);
}
function prettyRange(start, end) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const [, sm, sd] = start.split("-");
  const [, em, ed] = end.split("-");
  return `${months[+sm - 1]} ${+sd} – ${months[+em - 1]} ${+ed}`;
}

export function RangePayCard({ token }) {
  const [start, setStart] = useState(() => addDaysISO(todayISO(), -13));
  const [end, setEnd] = useState(() => todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

  const run = async () => {
    if (!start || !end) return;
    if (end < start) {
      setError("End date must be on or after the start date.");
      setData(null);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await adminApi(token).get("/admin/labor/range", {
        params: { start, end },
      });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load that range.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    if (!start || !end) return;
    setExporting(true);
    try {
      const res = await adminApi(token).get("/admin/labor/range", {
        params: { start, end, format: "csv" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll-${start}-to-${end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4" data-testid="range-pay-card">
      <div className="flex items-center gap-2 mb-3">
        <CalendarRange className="w-5 h-5 text-brand-red" />
        <h3 className="font-display text-brand-red text-xl leading-none">Custom Range Pay</h3>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-3">
        <label className="text-xs">
          <div className="uppercase tracking-widest text-stone-500 font-bold mb-1">From</div>
          <input
            type="date"
            value={start}
            max={end || undefined}
            onChange={(e) => setStart(e.target.value)}
            data-testid="range-pay-start"
            className="px-2 py-1.5 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
          />
        </label>
        <label className="text-xs">
          <div className="uppercase tracking-widest text-stone-500 font-bold mb-1">To</div>
          <input
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => setEnd(e.target.value)}
            data-testid="range-pay-end"
            className="px-2 py-1.5 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
          />
        </label>
        <button
          onClick={run}
          disabled={loading}
          data-testid="range-pay-run"
          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-stone-900 text-white text-[11px] uppercase tracking-widest font-black hover:bg-stone-700 disabled:opacity-40"
        >
          <Search className="w-3.5 h-3.5" />
          {loading ? "…" : "Show"}
        </button>
        <button
          onClick={exportCsv}
          disabled={exporting || loading || !data || data.employees.length === 0}
          data-testid="range-pay-export"
          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-brand-red text-white text-[11px] uppercase tracking-widest font-black hover:bg-red-800 disabled:opacity-40"
        >
          <Download className="w-3.5 h-3.5" />
          {exporting ? "…" : "CSV"}
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 mb-2" data-testid="range-pay-error">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="text-sm text-stone-600">
              <span className="font-bold text-stone-800">{prettyRange(data.start, data.end)}</span>
              <span className="text-stone-400 text-xs ml-1">
                ({data.day_count} {data.day_count === 1 ? "day" : "days"} · {data.timezone})
              </span>
            </div>
            <div
              className="flex items-center gap-2 bg-stone-900 text-brand-yellow rounded-xl px-4 py-2"
              data-testid="range-pay-total"
            >
              <DollarSign className="w-4 h-4" />
              <div>
                <div className="text-[9px] uppercase tracking-widest opacity-80 leading-none">Total owed</div>
                <div className="font-mono text-xl font-black leading-tight">{money(data.total_pay)}</div>
              </div>
              <div className="ml-3 border-l border-white/20 pl-3">
                <div className="text-[9px] uppercase tracking-widest opacity-80 leading-none">Hours</div>
                <div className="font-mono text-xl font-black leading-tight">{data.total_hours}</div>
              </div>
            </div>
          </div>

          {data.employees.length === 0 ? (
            <div className="py-6 text-center text-stone-400 text-sm" data-testid="range-pay-empty">
              No clocked hours in this range.
            </div>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm border-collapse min-w-[360px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-stone-500">
                    <th className="text-left font-black py-2 pr-2">Employee</th>
                    <th className="text-right font-black px-2 py-2">Hours</th>
                    <th className="text-right font-black px-2 py-2">Rate</th>
                    <th className="text-right font-black pl-2 py-2">Owed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.employees.map((e) => (
                    <tr
                      key={e.employee_id || e.name}
                      className="border-t border-stone-100"
                      data-testid={`range-pay-row-${e.name}`}
                    >
                      <td className="py-2 pr-2 font-bold text-stone-800 whitespace-nowrap">{e.name}</td>
                      <td className="text-right px-2 py-2 font-mono font-bold">{e.hours.toFixed(2)}</td>
                      <td className="text-right px-2 py-2 font-mono text-stone-500">{money(e.pay_rate)}</td>
                      <td className="text-right pl-2 py-2 font-mono font-black text-brand-red">{money(e.pay)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-stone-200 text-stone-900">
                    <td className="py-2 pr-2 font-black">Total</td>
                    <td className="text-right px-2 py-2 font-mono font-black">{data.total_hours}</td>
                    <td></td>
                    <td className="text-right pl-2 py-2 font-mono font-black text-brand-red">{money(data.total_pay)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <p className="text-[11px] text-stone-400 mt-2">
            Straight-time total at each employee's pay rate. Overtime (1.5×) is applied in the weekly view only.
          </p>
        </>
      )}
    </div>
  );
}
